<template>
  <div class="builder-screen fade-in" style="height: 100vh;">
    <transition name="fade-slide">
      <div v-if="isLoading" class="global-loader">
        <div class="loader-inner">

          <!-- NEW CENTERED DOODLE -->
          <svg class="loader-doodle" viewBox="0 0 500 260" xmlns="http://www.w3.org/2000/svg">

            <!-- Main screen -->
            <rect x="60" y="40" width="380" height="180" rx="16" fill="#ffffff" stroke="#e5e7eb" stroke-width="2" />

            <!-- Header bar -->
            <rect x="60" y="40" width="380" height="32" rx="16" fill="var(--brand)" opacity="0.15">
            </rect>

            <!-- Floating left panel -->
            <rect class="float-a" x="40" y="100" width="80" height="40" rx="8" fill="var(--brand)" opacity="0.12" />

            <!-- Floating right panel -->
            <rect class="float-b" x="380" y="120" width="80" height="50" rx="10" fill="#6366F1" opacity="0.15" />

            <!-- Gear -->
            <g class="gear" transform="translate(250,145)">
              <circle cx="0" cy="0" r="14" fill="#fff" stroke="#cbd5e1" stroke-width="2" />
            </g>

            <!-- Cursor -->
            <polygon class="cursor" points="120,120 135,155 145,147" fill="#111" opacity="0.8"></polygon>

          </svg>

          <!-- NEW TEXT -->
          <h2 class="loader-title">Preparing Your Website</h2>
          <p class="loader-desc">AI is generating optimized content and placing designs perfectly.</p>

          <div class="dots">
            <span class="dot d1"></span>
            <span class="dot d2"></span>
            <span class="dot d3"></span>
          </div>

        </div>
      </div>
    </transition>

    <!-- ==== TOP HEADER BAR ==== -->
    <div class="builder-header">

      <div class="title-area">
        <h2 class="title"><i class='bx bxs-edit'></i> Website Builder</h2>

        <div class="device-toggle" style="margin-left: 30rem;">
          <button :class="{active: device === 'desktop'}" @click="device='desktop'" title="Desktop View">
            <i class='bx bx-desktop'></i>
          </button>
          <button :class="{active: device === 'tablet'}" @click="device='tablet'" title="Tablet View">
            <i class='bx bx-tab'></i>
          </button>
          <button :class="{active: device === 'mobile'}" @click="device='mobile'" title="Mobile View">
            <i class='bx bx-mobile-alt'></i>
          </button>
          <button class="btn-secondary subtle" style="margin-left: 1rem;" @click="undo">
            <i class="bx bx-undo"></i>
          </button>

          <button class="btn-secondary subtle" @click="redo">
            <i class="bx bx-redo"></i>
          </button>
        </div>
      </div>

      <div class="header-actions">
        <button class="btn-secondary subtle" @click="gotohome">
          <i class="bx bx-undo"></i> Go back to home
        </button>
        <button class="btn-secondary subtle" @click="resetTemplate">
          <i class="bx bx-undo"></i> Reset
        </button>
        <button class="btn-primary strong" @click="exportAndPublishHTML">
          <i class="bx bx-save"></i> Save & Publish
        </button>
      </div>
    </div>

    <!-- ==== MAIN CONTENT AREA ==== -->
    <div class="builder-body">

      <!-- ===== Live Preview Panel ===== -->
      <div class="preview-area card" :class="device" style="text-align: center;">


        <iframe ref="previewFrame" class="preview-live smooth"></iframe>
      </div>

      <!-- ===== SIDE EDITOR PANEL ===== -->
      <div class="editor-sidebar card" :class="{ collapsed: isSidebarCollapsed }">

        <!-- TAB MENU -->
        <div class="tab-row">
          <div v-for="tab in tabs" :key="tab.id" :class="['tab-item', activeTab === tab.id ? 'active' : '']"
            @click="activeTab = tab.id">
            <i :class="tab.icon"></i>
            <span>{{ tab.label }}</span>
          </div>
        </div>

        <!-- TAB CONTENT SECTION -->
        <div class="tab-content theme-scroll">

          <!-- SECTION REORDER TAB -->
          <div v-if="activeTab === 'sections'" class="sections-ui-wrap">

            <div class="section-head">
              <h3><i class="bx bx-grid-alt"></i> Manage Page Sections</h3>
              <p>Click to select + reorder, toggle visibility or reset layout</p>
            </div>

            <div class="section-list" style="margin-top:1rem;">
              <div v-for="(section, index) in sections" :key="section.id" class="section-item"
                :class="{ 'active-section': selectedSwapIndex === index, 'hidden-section': section.hidden }"
                @click="selectSectionForSwap(index)">
                <div class="left-col">

                  <span class="label">{{ section.label }}</span>
                </div>

                <button class="toggle-visibility-btn" :title="section.hidden ? 'Show Section' : 'Hide Section'"
                  @click.stop="toggleSection(section)">
                  <i :class="section.hidden ? 'bx bx-hide' : 'bx bx-show'"></i>
                </button>
              </div>
            </div>


            <div class="section-actions">
              <button class="btn-secondary ghost" @click="resetSections">
                <i class="bx bx-history"></i> Reset Layout
              </button>
              <button class="btn-primary" @click="applySectionOrder">
                <i class="bx bx-check-circle"></i> Apply Changes
              </button>
            </div>
          </div>
          <div v-if="activeTab === 'plans'" class="sections-ui-wrap">

            <!-- ===== HEADER ===== -->
            <div class="section-head">
              <h3><i class="bx bx-grid-alt"></i> Manage Layouts</h3>
              <p>Organize page sections and adjust layout behavior.</p>
            </div>

            <!-- ===== HORIZONTAL FLIP CONTROL ===== -->
            <div class="mb-4 p-3 rounded-lg border border-gray-200 bg-white shadow-sm flex flex-col gap-2">
              <label class="font-semibold text-sm flex items-center gap-2">
                <i class="bx bx-transfer-alt text-lg"></i>
                Flip Selected Section Layout
              </label>

              <button
                class="btn-secondary ghost flex items-center justify-center gap-2 py-2.5 rounded-md border border-gray-300 hover:bg-gray-100 transition"
                @click="toggleHorizontalFlip" style="margin-top: 1rem;">
                <i class="bx bx-refresh-horizontal text-xl"></i>
                Flip Left ⇄ Right
              </button>
            </div>

            <!-- ===== CALENDAR LINK INPUT ===== -->
            <div style="margin-bottom: 16px; width: 100%;">

              <label style="
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      font-weight: 600;
      margin-top: 1rem;
      color: #374151;
      margin-bottom: 4px;
    ">
                <i class="bx bx-calendar-event" style="font-size: 18px;"></i>
                Calendar Booking Link
              </label>

           

                <select style="
      width: 100%;
      padding: 10px 14px;
      font-size: 14px;
      margin-top: 1rem;
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #111827;
      border-radius: 8px;
      outline: none;
      transition: all 0.2s ease;
      box-sizing: border-box;
    " onfocus="this.style.border='1px solid #000'; this.style.boxShadow='0 0 0 2px rgba(0,0,0,0.2)';" v-model="calenderLink">
            <option value="">Not needed</option>
            <option  v-for="step in cals"
            :key="step.booking_page_id" :value="base_url1+'appointment/embed/'+step.booking_page_url">{{base_url1}}appointment/embed/{{ step.booking_page_url }}</option>
          </select>

            </div>


            <!-- ===== LAYOUT PLAN SELECTOR ===== -->
            <div class="section-head">
              <h3><i class="bx bx-layer"></i> Membership Layouts</h3>
              <p>Select which pricing layout should be visible.</p>
            </div>

            <div class="section-list mt-3">
              <div v-for="(section, index) in planLayouts" :key="section.id" class="section-item"
                :class="{ 'active-section': planPos === index, 'hidden-section': section.hidden }"
                @click="toggleSectionPlan(index)">
                <div class="left-col">
                  <span class="label">{{ section.label }}</span>
                </div>

                <button class="toggle-visibility-btn" :title="section.hidden ? 'Show Section' : 'Hide Section'"
                  @click.stop="toggleSectionPlan(index)">
                  <i :class="section.hidden ? 'bx bx-hide' : 'bx bx-show'"></i>
                </button>
              </div>
            </div>

            <!-- ===== ACTION BUTTONS ===== -->
            <!-- <div class="section-actions mt-4">
              <button class="btn-secondary ghost" @click="resetSections">
                <i class="bx bx-history"></i> Reset Layout
              </button>

              <button class="btn-primary" @click="applySectionOrder">
                <i class="bx bx-check-circle"></i> Apply Changes
              </button>
            </div> -->

          </div>



          <!-- OTHER EDITOR TABS LOADED HERE -->
          <component v-if="activeTab !== 'sections'" :is="activeComponent" :selectedElement="selectedElement"
            :navItems="menuItems" ref="menuEditor" @updateText="handleEditorUpdate" @updateImage="handleEditorUpdate"
            @updateButton="handleEditorUpdate" @updateMenu="handleEditorUpdate"
            @removeElement="removeSelectedElement" />
        </div>
      </div>
      <button class="sidebar-toggle-btn" @click="toggleSidebar">
        <i v-if="!isSidebarCollapsed" class="bx bx-chevron-right"></i>
        <i v-else class="bx bx-chevron-left"></i>
      </button>

    </div>
    <ConfirmDialog :show="showConfirm" :title="confirmTitle" :message="confirmMessage" @cancel="showConfirm = false"
      @confirmAction="handleConfirm" />
  </div>
</template>

<script>
  import axios from "axios";
  import draggable from "vuedraggable";
  import { getWebById, getWebContent, publishWeb, getWebHtml } from "@/apis/webapis";
  import ConfirmDialog from "@/comps/ConfirmDialog.vue";
  import { apiBaseUrl } from "@/apis/appconst";
  import TabTextEditor from "@/comps/editor/tabs/TabTextEditor.vue";
  import TabMediaEditor from "@/comps/editor/tabs/TabMediaEditor.vue";
  import TabMenuManager from "@/comps/editor/tabs/TabMenuManager.vue";
  import TabButtonEditor from "@/comps/editor/tabs/TabButtonEditor.vue";
  import { useToast } from "vue-toastification";
  import { getBookPages } from "@/apis/bookpageapis";
  import { mainBaseUrl } from "@/apis/appconst";
  const toast = useToast();

  export default {
    name: "WebsiteEditor",
    components: {
      draggable,
      TabTextEditor,
      TabMediaEditor,
      TabMenuManager,
      TabButtonEditor,
      ConfirmDialog
    },

    data() {
      return {
        calenderLink: "",
        base_url1:"",
        isSidebarCollapsed: false,
        isLoading: false,
        showConfirm: false,
        webId: "",
        confirmTitle: "",
        confirmMessage: "",
        webData: null,
        webScript: null,
        menuItems: [],
        plans: [],
        cals:[],
        selectedMenu: [],
        undoStack: [],
        redoStack: [],
        maxHistory: 25,
        previewUrl: "/templates/t1.html",
        chatBotUrl: "/templates/t1data/chatbot.html",
        plansUrl: [
          "/templates/t1data/plan1.html",
          "/templates/t1data/plan2.html",
          "/templates/t1data/plan3.html",
        ],
        planPos: 0,
        loading: true,
        htmlContent: "",         // Loaded HTML
        originalHTML: "",
        htmlContentPlan: "",         // Loaded HTML
        originalHTMLPlan: "",
        htmlContentChat: "",         // Loaded HTML
        originalHTMLChat: "",        // Backup for reset
        selectedElement: null,   // Active selected DOM element
        activeTab: "sections",
        device: "desktop",
        tabs: [
          { id: "text", label: "Text", icon: "bx bx-font" },
          { id: "media", label: "Images", icon: "bx bx-image" },
          { id: "sections", label: "Sections", icon: "bx bx-grid-alt" },
          { id: "buttons", label: "Buttons", icon: "bx bx-pointer" },
          { id: "plans", label: "Layout", icon: "bx bx-user" }
        ],

        sections: [],
        planLayouts: [
          { id: 0, label: "Plan 1", hidden: false },
          { id: 1, label: "Plan 2", hidden: true },
          { id: 2, label: "Plan 3", hidden: true }
        ],
        selectedSwapIndex: null,
      };
    },

    async mounted() {
      const segments = window.location.pathname.split("/").filter(seg => seg !== "");
      this.webId = segments[segments.length - 1];
      this.base_url1 = mainBaseUrl;
      await this.loadData();
      await this.fetchPages();
      // alert(this.webData.website_headline+"");
      if (this.webData.website_headline != null && this.webData.website_headline != undefined && this.webData.website_headline.length > 0) {
        this.calenderLink = this.webData.website_headline;
      }
      if (
        // this.webData.website_status==="Live" &&   
        this.webData.website_page.length > 0) {
        this.previewUrl = this.webData.website_page;

        await this.loadHtmlTemplate(1);
        await this.loadHtmlTemplateChat();
      }
      else {

        await this.loadHtmlTemplate(0);
        await this.loadHtmlTemplatePlan();
        await this.loadHtmlTemplateChat();
      }
      this.loadData1();
    },

    computed: {
      activeComponent() {
        const map = {
          text: TabTextEditor,
          media: TabMediaEditor,
          buttons: TabButtonEditor,
          menu: TabMenuManager
        };
        return map[this.activeTab] || null;
      }
    },

    watch: {
      selectedElement(newEl) {
        if (!newEl) return;
        const tag = newEl.tagName.toLowerCase();

        if (tag === "img") this.activeTab = "media";
        else if (tag === "button" || newEl.classList.contains("btn")) this.activeTab = "buttons";
        else this.activeTab = "text";
      }
      ,
      calenderLink(newVal) {
        console.log("sda",newVal)
        this.$nextTick(() => {
          this.updateCalendarInIframe();
        });
      }
    },

    

    methods: {
         async fetchPages() {
      // const toast = useToast();
      try {
       
        const res = await getBookPages(
          this.webData?.website_user,
          "none",
          "",
          "",
          ""
        );
        if (res.status && res.records?.length) {
          this.cals = res.records;
        } else {
          this.cals = [];
          // toast.success(res.success || "No pages found");
        }
      } catch (error) {
        console.error("fetchPages error:", error);
        // toast.error("Failed to load pages");
      }
    },
      injectRealPlans(doc) {
        const container = doc.querySelector("[data-web-field='website_plans_static']");
        if (!container) return;

        container.innerHTML = "";

        this.plans.forEach((p, index) => {
          console.log("sdffwf", index);
          // Fix features JSON
          let features = [];

            try {
              if (p.website_plan_features) {

                // If JSON array string
                if (p.website_plan_features.trim().startsWith("[")) {
                  features = JSON.parse(p.website_plan_features);
                }

                // Else comma separated string
                else if (p.website_plan_features.includes(",")) {
                  features = p.website_plan_features.split(",");
                }

              }
            } catch (e) {
              features = [];
            }

          const featureHTML = features
            .filter(f => f && f.trim() !== "")
            .map(f => `
                <li class="flex items-center gap-2">
                  <i class='bx bx-check text-[var(--brand)]'></i> ${f}
                </li>
              `)
            .join("");

          const card = doc.createElement("div");
          card.className = "pricing-card rounded-xl p-8 bg-white shadow-md hover:shadow-lg transition";
          card.setAttribute("data-plan-id", p.website_plan_id);

          card.innerHTML = `
              <h3 class="text-xl font-bold mb-2">
                ${p.website_plan_title || "Untitled Plan"}
              </h3>

              <p class="text-gray-500 text-sm mb-4">
                ${p.website_plan_desc || "No description provided."}
              </p>

              <h2 class="text-3xl font-extrabold mb-4">
                ${p.website_plan_curr || "INR"} ${p.website_plan_price || "0"}
                <span class="text-sm text-gray-500">/${p.website_plan_type || "Monthly"}</span>
              </h2>

              <ul class="space-y-2 text-sm text-gray-700 mb-6">
                ${featureHTML || "<li>No features added</li>"}
              </ul>

              <button class="w-full py-3 rounded-lg text-white font-semibold"
                      style="background: var(--brand);">
                Choose Plan
              </button>
            `;

          container.appendChild(card);
        });
      }
      ,
      toggleSidebar() {
        this.isSidebarCollapsed = !this.isSidebarCollapsed;
      },
      saveHistory() {
        const iframe = this.$refs.previewFrame;
        if (!iframe) return;

        const doc = iframe.contentDocument || iframe.contentWindow.document;
        if (!doc) return;

        const html = doc.documentElement.outerHTML;

        // Avoid saving duplicate consecutive states
        if (this.undoStack.length > 0 && this.undoStack[this.undoStack.length - 1] === html) {
          return;
        }

        // Push into undo stack
        this.undoStack.push(html);

        // Limit to maxHistory
        if (this.undoStack.length > this.maxHistory) {
          this.undoStack.shift();
        }

        // Clear redo when new action happens
        this.redoStack = [];
      },
      undo() {
        if (this.undoStack.length === 0) {
          toast.success("Nothing to undo");
          return;
        }

        const iframe = this.$refs.previewFrame;
        const doc = iframe.contentDocument || iframe.contentWindow.document;

        // Current state → move to redo
        const current = doc.documentElement.outerHTML;
        this.redoStack.push(current);

        // Restore previous state
        const previous = this.undoStack.pop();
        this.renderIframe(previous);

        toast.success("Undo applied");
      }
      ,
      redo() {
        if (this.redoStack.length === 0) {
          toast.success("Nothing to redo");
          return;
        }

        const iframe = this.$refs.previewFrame;
        const doc = iframe.contentDocument || iframe.contentWindow.document;

        // Move current into undo
        const current = doc.documentElement.outerHTML;
        this.undoStack.push(current);

        // Restore redo snapshot
        const next = this.redoStack.pop();
        this.renderIframe(next);

        toast.success("Redo applied");
      }

      ,
      toggleHorizontalFlip() {
        this.saveHistory();
        try {

          if (!this.selectedElement) {
            toast.error("Please select a section first.");
            return;
          }

          const section = this.selectedElement.closest("[data-section-id]");

          if (!section || !section.hasAttribute("data-section-id")) {
            toast.error("Please select the section (not inner elements).");
            return;
          }

          const containers = section.querySelectorAll("div, section");
          let row = null;

          containers.forEach(c => {
            const cs = getComputedStyle(c);

            if (cs.display === "flex" &&
              cs.flexDirection === "row" &&
              c.children.length >= 2) {
              row = c;
            }

            if (cs.display === "grid" && c.children.length >= 2) {
              row = c; // ANY grid qualifies
            }
          });

          if (!row) {
            toast.error("This section cannot be flipped (no multi-item row/grid).");
            return;
          }

          const children = [...row.children];
          const isFlipped = row.getAttribute("data-swap") === "true";

          // RESTORE
          if (isFlipped) {
            children
              .sort((a, b) => (a._originalIndex || 0) - (b._originalIndex || 0))
              .forEach(child => row.appendChild(child));

            row.setAttribute("data-swap", "false");
            // toast.success("Section restored.");
            return;
          }

          // Save original index ONCE
          children.forEach((child, i) => {
            if (child._originalIndex === undefined) {
              child._originalIndex = i;
            }
          });

          // NEW FIX → reverse ALL children for any grid or flex row
          [...children].reverse().forEach(child => row.appendChild(child));

          row.setAttribute("data-swap", "true");
          // toast.success("Section flipped!");

        } catch (err) {
          console.error("Flip error:", err);
          // toast.error("Flip operation failed. Check console.");
        }
      }

      ,
      _injectFlipStyle(doc) {
        if (doc.getElementById("flip-helper-styles")) return;

        const style = doc.createElement("style");
        style.id = "flip-helper-styles";

        style.innerHTML = `
        /* Helps flex/grid sections behave correctly after DOM flip */
        .section-flipped > * {
            width: 100%;
        }
        .section-flipped {
            display: block;
        }
    `;

        doc.head.appendChild(style);
      }
      ,
      updateCalendarInIframe() {
        this.saveHistory();
        const iframe = this.$refs.previewFrame;
        const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
        if (!doc) return;
        const calSection = doc.querySelector('#calancer[data-section-id="calancer"]');
        if (!calSection) return;

        const frame = calSection.querySelector('[data-web-field="booking_calander_link"]');
        if (!frame) return;

        // If link exists -> show section
        if (this.calenderLink && this.calenderLink.trim().length > 0) {
          calSection.style.display = "block";
          frame.src = this.calenderLink.trim();
        }
        else {
          // If empty -> hide section
          calSection.style.display = "none";
          frame.src = "";
        }
      }
      ,
      downloadFinalHTML() {
        const html = this.publishTemplate();

        const blob = new Blob([html], { type: "text/html" });
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "website.html";
        a.click();

        URL.revokeObjectURL(url);
      },
      exportAndPublishHTML() {
        this.openConfirm(
          "Publish Website?",
          `Are you sure you want to publish?`,
          async () => {
            try {
              var finalHTML = this.publishTemplate();
              try {
                finalHTML = finalHTML.replace(
                  /<style[^>]*id=["']editor-runtime-style["'][\s\S]*?<\/style>/gi,
                  ""
                );
              } catch (e) {
                console.warn("Placeholder replace failed1", e);
              }

              try {
                finalHTML = finalHTML
                  .replace(/editor-runtime-style/gi, "")
                  .replace(/dynamicHoverStyles/gi, "");
              } catch (e) {
                console.warn("Placeholder replace failed1", e);
              }


              try {
                finalHTML = finalHTML
                  .replace(/{{website_id}}/g, this.webId)
                  .replace(/{{website_user}}/g, this.webData?.website_user || "");
              } catch (e) {
                console.warn("Placeholder replace failed", e);
              }


              const blob = new Blob([finalHTML], { type: "text/html" });
              const file = new File([blob], "index.html", { type: "text/html" });

              this.isLoading = true;
              const res = await publishWeb(this.webId, file);

              this.isLoading = false;
              if (res.status) {
                // toast.success("Website HTML Published Successfully!");
                window.open(apiBaseUrl + res.website_dt.website_page, "_blank");

                setTimeout(() => {
                  window.location.href = "/my_websites";
                }, 2 * 1000);
              } else {
                toast.error(res.success || "Publishing failed");
              }


            } catch (err) {
              console.error("Publish HTML Error:", err);
              toast.error("Network or Server Error");
            }
          }
        );
      },
      confirmCallback() { },
      async openConfirm(title, message, onConfirm) {
        this.confirmTitle = title;
        this.confirmMessage = message;
        this.confirmCallback = onConfirm;
        this.showConfirm = true;
      },
      async handleConfirm() {
        this.showConfirm = false;

        if (typeof this.confirmCallback === "function") {
          await this.confirmCallback();
        }

        this.confirmCallback = null;
      }
      ,
      injectPlanHTML(doc) {
        const section = doc.querySelector('#membership_plan[data-section-id="membership_plan"]');
        if (section) {
          section.innerHTML = this.htmlContentPlan;
          section.style.display = "block";
        }

        const section1 = doc.querySelector('#chatbot45[data-section-id="chatbot45"]');
        if (section1) {
          section1.innerHTML = this.htmlContentChat;

          section1.style.display = "none";
        }
        this.injectRealPlans(doc);
      },
      async loadData() {
        this.isLoading = true;
        const res = await getWebById(this.webId);
        this.isLoading = false;
        if (res != null) {
          this.webData = res.record;
          this.webScript = res.web_script;
          this.plans = res.plans;
        }
      },
      async loadData1() {

        const iframe = this.$refs.previewFrame;
        if (!iframe) return;
        const doc = iframe.contentDocument || iframe.contentWindow.document;
        this.loadCalLink(doc);
        // alert(this.webData.website_status+"");    
        if (this.webData.website_status == 'Pending' || this.webData.website_status == 'In Progress') {
          if (!doc) return;
          // if (doc && this.htmlContentPlan) {
          //   setTimeout(() => {
          //     this.injectPlanHTML(doc);
          //   }, 1 * 1000);
          // }
          this.applyWebsiteContent(doc);

          if (this.webData.website_page == null || this.webData.website_page.length <= 0) {
            this.isLoading = true;
            setTimeout(() => {
              this.getAiContent(doc);
            }, 1 * 2000);
          }
        }
        else {

          setTimeout(() => {
            this.injectPlanHTML(doc);
            const section1 = doc.querySelector('#chatbot45[data-section-id="chatbot45"]');
            if (section1) section1.style.display = "none";
          }, 1 * 3000);
        }

      },
      async loadAiData(content_length, content_type) {

        const res = await getWebContent(this.webId, content_length, content_type);
        if (res && res.status && res.content) {
          return res.content;
        }
        return "";
      },
      // async getAiContent(doc) {

      //   const aiNodes = [...doc.querySelectorAll("[data-content]")];
      //   this.isLoading = true;
      //   for (const el of aiNodes) {

      //     const key = el.getAttribute("data-content");
      //     const type = el.getAttribute("data-content-type");

      //     if (!key || !type) continue;

      //     const currentText = (el.innerText || el.textContent || "").trim();
      //     const length = currentText.length || 50; // safe fallback

      //     try {
      //       el.innerHTML = await this.loadAiData(length, type);
      //     } catch (err) {
      //       console.warn("AI content error for", key, err);
      //     }
      //   }
      //   this.isLoading = false;
      // },
      async getAiContent(doc) {
        const aiNodes = [...doc.querySelectorAll("[data-content]")];
        this.isLoading = true;

        for (const el of aiNodes) {
          const key = el.getAttribute("data-content");
          const type = el.getAttribute("data-content-type");

          if (!key || !type) continue;

          try {

            // 🔹 ICON HANDLING ONLY
            if (key.startsWith("ai_icon_")) {
              const iconName = await this.loadAiData(20, type);
              // example return: "bx-calendar"

              if (iconName && iconName.startsWith("bx-")) {
                // remove old bx-* icon classes
                el.className = el.className
                  .split(" ")
                  .filter(c => !c.startsWith("bx-"))
                  .join(" ");

                // add new icon
                el.classList.add("bx", iconName);
              }

              continue; // ⛔ don't touch innerHTML
            }

            // 🔹 TEXT CONTENT (default)
            const currentText = (el.innerText || el.textContent || "").trim();
            const length = currentText.length || 50;

            el.innerHTML = await this.loadAiData(length, type);

          } catch (err) {
            console.warn("AI content error for", key, err);
          }
        }

        this.isLoading = false;
      }
      ,
      loadCalLink(doc) {
        setTimeout(() => {
          try {
            const calSection = doc.querySelector('#calancer[data-section-id="calancer"]');
            if (calSection) {
              if (this.calenderLink && this.calenderLink.length > 0) {
                calSection.style.display = "block";

                const iframe = calSection.querySelector('[data-web-field="booking_calander_link"]');
                if (iframe) {
                  iframe.src = this.calenderLink;
                }
              } else {
                const iframe = calSection.querySelector('[data-web-field="booking_calander_link"]');
                const rawSrc = iframe?.getAttribute("src") || "";

                if (rawSrc.trim().length > 0) {
                  this.calenderLink = rawSrc.trim();
                  calSection.style.display = "block";
                }
                else {
                  calSection.style.display = "none";
                }
              }
            }
          } catch (err) {
            console.warn("AI content error for", err);
          }
        }, 3000);

      }
      ,
      applyWebsiteContent(doc) {
        try {
          if (!doc) return;

          const data = this.webData;
          if (!data) return;

          /* ----------------------------------
             SAFE SCRIPT REPLACEMENT
          ---------------------------------- */
          // try {
          //   const scriptTags = doc.querySelectorAll("script");
          //   scriptTags.forEach((tag) => {
          //     try {
          //       if (!tag || !tag.innerHTML) return;
          //       if (tag.innerHTML.includes("{{website_id}}") || tag.innerHTML.includes("{{website_user}}")) {
          //         tag.innerHTML = tag.innerHTML
          //           .replace(/{{website_id}}/g, this.webId)
          //           .replace(/{{website_user}}/g, data.website_user || "");
          //       }
          //     } catch (err) {
          //       console.warn("Script tag update failed:", err);
          //     }
          //   });
          // } catch (err) {
          //   console.warn("Script processing failed:", err);
          // }


          /* ----------------------------------
             PARSE GALLERY IMAGES
          ---------------------------------- */
          let galleryImages = [];

          try {
            if (data.website_images) {
              const parsed = JSON.parse(data.website_images);
              if (Array.isArray(parsed)) {
                galleryImages = parsed.map(img => img ? apiBaseUrl + "/" + img : "");
              }
            }
          } catch (err) {
            console.warn("Invalid website_images JSON:", err);
          }

          /* ----------------------------------
             PROCESS data-web-field ELEMENTS
          ---------------------------------- */
          let nodes = [];
          try {
            nodes = doc.querySelectorAll("[data-web-field]");
          } catch (err) {
            console.warn("Query selector failed:", err);
          }

          nodes.forEach((el) => {
            try {
              if (!el) return;

              const key = el.getAttribute("data-web-field");
              if (!key) return;
              if (!(key in data)) return;

              const value = data[key];

              /* LOGO FIX */
              if (key === "website_logo") {
                if (el.tagName === "IMG") {
                  el.src = apiBaseUrl + "/" + value;
                }
                return;
              }

              /* FAVICON FIX */
              if (key === "website_fav") {
                if (el.tagName === "LINK") {
                  el.href = apiBaseUrl + "/" + value;
                }
                return;
              }

              /* SKIP GALLERY */
              if (key === "website_images") return;

              /* IMAGE TAGS */
              if (el.tagName === "IMG") {
                if (value) {
                  el.src = value.includes("uploads") ? apiBaseUrl + "/" + value : value;
                }
                return;
              }

              /* LINK TAGS */
              if (el.tagName === "A") {
                if (value) {
                  el.innerText = value;
                  el.href = value;
                }
                return;
              }

              /* INPUTS */
              if (["INPUT", "TEXTAREA"].includes(el.tagName)) {
                el.value = value || "";
                el.placeholder = value || "";
                return;
              }

              /* GENERAL TEXT CONTENT */
              if (value) {
                el.innerHTML = value;
              }

            } catch (err) {
              console.warn("Error updating data-web-field:", err);
            }
          });

          /* ----------------------------------
             GALLERY RENDERING
          ---------------------------------- */
          try {
            const galleryWrapper = doc.querySelector('[data-web-field="website_images"]');

            if (galleryWrapper && galleryImages.length > 0) {
              galleryWrapper.innerHTML = "";

              galleryImages.forEach((src) => {
                try {
                  const div = doc.createElement("div");
                  div.className = "rounded-xl overflow-hidden hover-lift fade h-[160px] bg-gray-200";

                  const img = doc.createElement("img");
                  img.className = "w-full h-full object-cover";
                  img.loading = "lazy";
                  img.src = src;

                  div.appendChild(img);
                  galleryWrapper.appendChild(div);
                } catch (err) {
                  console.warn("Gallery image failed:", err);
                }
              });
            }
          } catch (err) {
            console.warn("Gallery wrapper update failed:", err);
          }

          /* ----------------------------------
             BRANDING
          ---------------------------------- */
          try {
            if (data.website_color) {
              doc.documentElement.style.setProperty("--brand", data.website_color);
            }
            if (data.website_font) {
              doc.body.style.fontFamily = data.website_font;
            }
          } catch (err) {
            console.warn("Branding update failed:", err);
          }

          /* ----------------------------------
             SEO
          ---------------------------------- */
          try {
            if (data.website_seo_title) {
              const titleTag = doc.querySelector("title");
              if (titleTag) titleTag.innerText = data.website_seo_title;
            }

            if (data.website_seo_desc) {
              let meta = doc.querySelector('meta[name="description"]');
              if (!meta) {
                meta = doc.createElement("meta");
                meta.name = "description";
                doc.head.appendChild(meta);
              }
              meta.setAttribute("content", data.website_seo_desc);
            }
          } catch (err) {
            console.warn("SEO update failed:", err);
          }

          console.log("✔ applyWebsiteContent executed safely with full fallback handling");

        } catch (err) {
          console.error("FATAL: applyWebsiteContent crashed:", err);
        }
      }


      ,
      hasEditableText(el) {
        if (!el) return false;

        const tag = el.tagName.toLowerCase();

        // Elements that should NEVER open text editor
        const blockedTags = ["img", "svg", "path", "button", "icon"];
        if (blockedTags.includes(tag)) return false;

        // Ignore menu items & structural containers
        const structuralTags = ["nav", "ul", "ol", "li", "form", "section", "article", "div"];
        if (structuralTags.includes(tag) && el.children.length > 0) return false;

        // Extract visible text
        const text = (el.innerText || "").trim();

        // No text → not editable
        if (!text || text.length < 2) return false;

        // If element contains only icon / SVG-like children
        const invalidChild = [...el.children].every(child =>
          ["i", "svg", "path", "icon"].includes(child.tagName?.toLowerCase())
        );
        if (invalidChild) return false;

        return true;
      }

      ,
      updateMenuItems(updatedMenu) {
        if (!this.selectedElement) return;
        const links = this.selectedElement.querySelectorAll("a");

        updatedMenu.forEach((menu, index) => {
          const link = links[index];
          if (!link) return;

          link.innerText = menu.label;
          link.style.display = menu.hidden ? "none" : "";
          link.href = menu.href;
        });

        this.menuItems = updatedMenu;
      }
      ,
      selectSectionForSwap(index) {
        if (this.selectedSwapIndex === null) {
          // first section selected
          this.selectedSwapIndex = index;
        } else if (this.selectedSwapIndex === index) {
          // clicked same item → reset
          this.selectedSwapIndex = null;
        } else {
          // swap
          const tmp = this.sections[this.selectedSwapIndex];
          this.sections[this.selectedSwapIndex] = this.sections[index];
          this.sections[index] = tmp;

          this.selectedSwapIndex = null;
          this.applySectionOrder(); // reorder DOM
        }
      }
      ,
      detectSectionsFromIframe() {
        const iframe = this.$refs.previewFrame;
        const doc = iframe?.contentDocument;
        if (!doc) return;

        const wrapper = doc.querySelector("[data-section-wrapper]");
        if (!wrapper) {
          console.warn("⚠ No section wrapper found!");
          this.sections = [];
          return;
        }

        // Fetch direct child sections only
        const nodes = [...wrapper.children].filter(el => el.hasAttribute("data-section-id"));

        this.sections = nodes.map(node => ({
          id: node.getAttribute("data-section-id"),
          label: node.getAttribute("data-section-id")
            .replace(/[-_]/g, " ")
            .replace(/\b\w/g, l => l.toUpperCase()),
          hidden: node.style.display === "none",
          el: node
        }));

        console.log("📌 Detected Sections:", this.sections);
      }

      ,
      applySectionOrder() {
        this.saveHistory();
        const iframe = this.$refs.previewFrame;
        const doc = iframe?.contentDocument;
        if (!doc) return;

        const wrapper = doc.querySelector("[data-section-wrapper]");
        if (!wrapper) {
          console.warn("⚠ No wrapper found while applying section order!");
          return;
        }

        // Reorder DOM based on updated array
        this.sections.forEach(section => {
          const node = doc.querySelector(`[data-section-id="${section.id}"]`);
          if (node) {
            wrapper.appendChild(node); // Moves element to correct position
            node.style.display = section.hidden ? "none" : ""; // show/hide
          }
        });

        // Re-trigger AOS after reorder
        setTimeout(() => this.initAOS(), 200);
      }

      ,
      toggleSection(section) {

        section.hidden = !section.hidden;
        this.applySectionOrder();
      },
      toggleSectionPlan(pos) {
        this.saveHistory();
        for (var x = 0; x < this.planLayouts.length; x++) {
          if (pos == x) {
            this.planLayouts[x].hidden = false;
          }
          else {
            this.planLayouts[x].hidden = true;
          }
        }
        if (this.planPos != pos) {
          this.planPos = pos;
          this.loadHtmlTemplatePlan().then(() => {

            const iframe = this.$refs.previewFrame;
            const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
            if (!doc) return;
            // alert("1");

            this.injectPlanHTML(doc);
            this.applyWebsiteContent(doc);
            this.initAOS();
            this.detectSectionsFromIframe();
          });
        }
      },
      resetSections() {
        this.saveHistory();
        this.renderIframe(this.originalHTML);
        setTimeout(() => {
          this.detectSectionsFromIframe();
        }, 300);
      },
      getAllSections() {
        const iframe = this.$refs.previewFrame;
        const doc = iframe.contentDocument;

        const nodes = [...doc.querySelectorAll("[data-section-id]")];
        return nodes.map(node => ({
          id: node.getAttribute("data-section-id"),
          label: node.getAttribute("data-section-id").replace("-", " ").toUpperCase(),
          hidden: node.style.display === "none",
          el: node
        }));
      }


      ,
      removeSelectedElement() {
        this.saveHistory();
        if (!this.selectedElement) return;

        const el = this.selectedElement;

        // Remove element safely from DOM
        el.remove();

        // Clear reference
        this.selectedElement = null;

        // Reset active tab to avoid dead panel
        this.activeTab = "sections";
      }
      ,
      initAOS() {
        const iframe = this.$refs.previewFrame;
        if (!iframe) return;

        const win = iframe.contentWindow;
        if (win && win.AOS) {
          win.AOS.init({ duration: 800, once: false });
          win.AOS.refreshHard();
        }
      }

      ,
      async loadHtmlTemplate(rtype) {
        this.loading = true;

        try {
          if (rtype == 1) {
            const html = await getWebHtml(this.previewUrl);

            this.htmlContent = html.html;
            this.originalHTML = html.html;
            this.renderIframe(html.html);
          }
          else {
            const response = await axios.get(this.previewUrl, {
              headers: { "Content-Type": "multipart/form-data" },
            });

            const html = await response.data + "";
            this.htmlContent = html;
            this.originalHTML = html;
            this.renderIframe(html);
          }
        } catch (err) {
          console.error("Failed to load template:", err);
        } finally {
          this.loading = false;
        }
      },
      async loadHtmlTemplatePlan() {
        this.loading = true;

        try {
          const res = await fetch(this.plansUrl[this.planPos], { cache: "no-cache" });
          const html = await res.text();
          this.htmlContentPlan = html;

        } catch (err) {
          console.error("Failed to load template:", err);
        } finally {
          this.loading = false;
        }
      },
      async loadHtmlTemplateChat() {
        this.loading = true;

        try {
          const res = await fetch(this.chatBotUrl, { cache: "no-cache" });
          const html = await res.text();
          this.htmlContentChat = html;
          this.htmlContentChat = html;

        } catch (err) {
          console.error("Failed to load template:", err);
        } finally {
          this.loading = false;
        }
      },
      renderIframe(html) {

        const iframe = this.$refs.previewFrame;
        const doc = iframe.contentDocument || iframe.contentWindow.document;

        doc.open();
        doc.write(html);
        doc.close();

        setTimeout(() => {
          this.attachIframeClickEvents(doc);
          this.injectIframeAssets(doc);

          // Run detection AFTER DOM is fully loaded
          this.detectSectionsFromIframe();
        }, 300);

        setTimeout(() => this.initAOS(), 500);
      }

      ,
      injectIframeAssets(doc) {
        const iframe = this.$refs.previewFrame;

        const assets = [
          { type: "style", url: "https://cdn.tailwindcss.com" },
          { type: "style", url: "https://cdn.jsdelivr.net/npm/aos@2.3.4/dist/aos.css" },
          { type: "style", url: "https://unpkg.com/boxicons@2.1.4/css/boxicons.min.css" },
          { type: "script", url: "https://cdn.jsdelivr.net/npm/aos@2.3.4/dist/aos.js" }
        ];

        assets.forEach(asset => {
          if (asset.type === "style") {
            const link = doc.createElement("link");
            link.rel = "stylesheet";
            link.href = asset.url;
            doc.head.appendChild(link);
          } else {
            const script = doc.createElement("script");
            script.src = asset.url;
            script.onload = () => {
              if (asset.url.includes("aos")) {
                iframe.contentWindow.AOS?.init();
              }
            };
            doc.body.appendChild(script);
          }
        });
      }

      ,
      attachIframeClickEvents(doc) {
        const iframeWin = this.$refs.previewFrame.contentWindow;

        const isImageEl = (el) => {
          if (!el) return false;
          if (el.tagName.toLowerCase() === "img") return true;
          const bg = iframeWin.getComputedStyle(el).backgroundImage;
          return bg && bg !== "none";
        };

        const isMenuEl = (el) => {
          if (!el) return false;
          const tag = el.tagName.toLowerCase();
          return (tag === "nav" || tag === "ul") && el.querySelectorAll("a").length > 0;
        };

        doc.addEventListener("click", (e) => {
          if (!e.target || e.target === doc.body) return;

          e.preventDefault();
          e.stopPropagation();

          if (this.selectedElement) this.selectedElement.style.outline = "";

          let target = e.target.nodeType === 3 ? e.target.parentElement : e.target;

          this.selectedElement = target;
          target.style.outline = "2px dashed #2563eb";
          // 🔹 FIX: input / textarea ke liye text source set karo
          if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") {
            target._editorText = target.value || target.placeholder || "";
          } else {
            target._editorText = target.innerText || "";
          }


          // TAB auto-switch
          if (isImageEl(target)) {
            this.activeTab = "media";
          }
          else if (isMenuEl(target)) {
            this.activeTab = "menu";

            // Extract menu links inside clicked nav/ul
            const links = target.querySelectorAll("a");

            this.menuItems = [...links].map(a => ({
              label: a.innerText.trim(),
              hidden: a.style.display === "none",
              href: a.getAttribute("href")?.trim() || "#",
              el: a
            }));

            // Save reference to selected menu container element
            this.selectedElement = target;

            // Pass data to menu editor component safely
            this.$nextTick(() => {
              if (this.$refs.menuEditor && typeof this.$refs.menuEditor.loadMenu === "function") {
                this.$refs.menuEditor.loadMenu(JSON.parse(JSON.stringify(this.menuItems)));
              }
            });
          }
          else {
            const tag = target.tagName.toLowerCase();

            // 🔹 INPUT / TEXTAREA → open TEXT tab but handle differently
            if (tag === "input" || tag === "textarea") {
              this.activeTab = "text";
              return;
            }
            // this.activeTab = "text";
            const hasRealText = this.hasEditableText(target);

            if (hasRealText) {
              this.activeTab = "text";
            } else {
              // Do NOT open text tab if no real text
              this.activeTab = "sections"; // or stay on previous tab
            }
          }
        }, true);
      }
      ,

      gotohome() {
        this.openConfirm(
          "Go back to home?",
          `Please be careful. Any step or changes will not be saved.`,
          async () => {
            window.location.href = "/";
          });
      },
      // Reset Template
      resetTemplate() {
        this.saveHistory();
        if (this.selectedElement) {
          this.selectedElement.style.outline = "";
        }
        this.selectedElement = null;
        this.renderIframe(this.originalHTML);
      }

      ,
      cleanScript(raw) {
        if (!raw) return "";
        let scr = raw.trim();
        if (scr.startsWith('"') && scr.endsWith('"')) {
          scr = scr.substring(1, scr.length - 1);
        }
        scr = scr.replace(/\\"/g, '"');
        scr = scr.replace(/\\\//g, '/');
        return scr;
      }
      ,
   publishTemplate() {

      const iframe = this.$refs.previewFrame;
      const doc = iframe.contentDocument || iframe.contentWindow.document;

      /* -------------------------------
        SHOW CHATBOT SECTION
      -------------------------------- */
      const section1 = doc.querySelector('#chatbot45[data-section-id="chatbot45"]');
      if (section1) section1.style.display = "block";

      /* -------------------------------
        REMOVE EDITOR SELECTION STYLES
      -------------------------------- */
      const outlined = doc.querySelectorAll("[style*='outline']");
      outlined.forEach(el => {
        el.style.outline = "";
        el.style.outlineOffset = "";
      });

      /* -------------------------------
        REMOVE contenteditable ATTR
      -------------------------------- */
      doc.querySelectorAll("[contenteditable]")
        .forEach(el => el.removeAttribute("contenteditable"));

      /* -------------------------------
        REMOVE EDITOR STYLE TAGS
      -------------------------------- */
      const editorStyle = doc.getElementById("editor-runtime-style");
      if (editorStyle) editorStyle.remove();

      const hoverStyle = doc.getElementById("dynamicHoverStyles");
      if (hoverStyle) hoverStyle.remove();

      /* -------------------------------
        INJECT REAL SCRIPT
      -------------------------------- */
      let rawScript = this.webScript?.web_script || "";

      if (rawScript && rawScript.length > 0) {

        let clean = this.cleanScript(rawScript);

        if (clean && !doc.head.innerHTML.includes(clean)) {

          const temp = document.createElement("div");
          temp.innerHTML = clean;

          const scriptTag = temp.querySelector("script");

          if (scriptTag) {

            const newScript = doc.createElement("script");

            [...scriptTag.attributes].forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });

            newScript.innerHTML = scriptTag.innerHTML;
            doc.head.appendChild(newScript);
          }
        }
      }

      /* -------------------------------
        RETURN FINAL HTML
      -------------------------------- */
      return doc.documentElement.outerHTML;
    }
    ,


      // Apply Editor Updates
      handleEditorUpdate(payload) {
        this.saveHistory();
        if (!this.selectedElement) return;
        const el = this.selectedElement;
        const iframeDoc = this.$refs.previewFrame.contentDocument;

        /* -------------------------
           TEXT & GENERAL ELEMENT UPDATE
        --------------------------*/
        if (payload.text !== undefined) {
          if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
            el.value = payload.text;
            el.placeholder = payload.text;
          } else {
            el.innerHTML = payload.text;
          }
        }



        if (payload.size) el.style.setProperty("font-size", payload.size + "px", "important");
        if (payload.color) el.style.setProperty("color", payload.color, "important");
        if (payload.bg) el.style.setProperty("background-color", payload.bg, "important");
        if (payload.weight) el.style.setProperty("font-weight", payload.weight, "important");
        if (payload.align) el.style.setProperty("text-align", payload.align, "important");

        /* -------------------------
           BUTTON STYLING UPDATE
        --------------------------*/
        if (payload.fontSize) el.style.setProperty("font-size", payload.fontSize + "px", "important");
        if (payload.textColor) el.style.setProperty("color", payload.textColor, "important");
        if (payload.bgColor) el.style.setProperty("background-color", payload.bgColor, "important");
        if (payload.padding) el.style.setProperty("padding", payload.padding, "important");
        if (payload.radius) el.style.setProperty("border-radius", payload.radius + "px", "important");

        if (payload.borderWidth) el.style.setProperty("border-width", payload.borderWidth + "px", "important");
        if (payload.borderColor) el.style.setProperty("border-color", payload.borderColor, "important");
        if (payload.borderStyle) el.style.setProperty("border-style", payload.borderStyle, "important");

        /* -------------------------
           HOVER STYLES SUPPORT
        --------------------------*/
        if (payload.hoverBgColor || payload.hoverTextColor) {
          const hoverClass = "generated-hover-style";
          el.classList.add(hoverClass);

          let hoverStyleTag = iframeDoc.getElementById("dynamicHoverStyles");
          if (!hoverStyleTag) {
            hoverStyleTag = iframeDoc.createElement("style");
            hoverStyleTag.id = "dynamicHoverStyles";
            iframeDoc.head.appendChild(hoverStyleTag);
          }

          hoverStyleTag.innerHTML = `
      .${hoverClass}:hover {
        ${payload.hoverBgColor ? `background-color: ${payload.hoverBgColor} !important;` : ""}
        ${payload.hoverTextColor ? `color: ${payload.hoverTextColor} !important;` : ""}
      }
    `;
        }

        /* -------------------------
           IMAGE UPDATE
        --------------------------*/
        if (payload.url !== undefined) {
          if (el.tagName.toLowerCase() === "img") {
            el.src = payload.url;
          } else {
            el.style.setProperty("background-image", payload.url ? `url('${payload.url}')` : "none", "important");
          }
        }

        /* -------------------------
           MENU UPDATE
        --------------------------*/
        if (payload.menu && Array.isArray(payload.menu)) {
          const links = el.querySelectorAll("a");

          payload.menu.forEach((item, index) => {
            const link = links[index];
            if (!link) return;

            if (item.label !== undefined) link.innerText = item.label;
            link.style.display = item.hidden ? "none" : "";
            if (item.href) link.setAttribute("href", item.href);
          });
        }

        this.initAOS();
      }
    }
  };
</script>



<style scoped>
  .global-loader {
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(6px);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99999;
    padding: 20px;
  }

  .loader-inner {
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  /* DOODLE SIZE */
  .loader-doodle {
    width: 280px;
    max-width: 70%;
    height: auto;
    animation: fadeIn 0.6s ease;
    filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.08));
  }

  /* TEXT STYLING */
  .loader-title {
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
    margin: 6px 0 0;
  }

  .loader-desc {
    font-size: 14px;
    color: #475569;
    max-width: 340px;
    line-height: 1.5;
  }

  /* DOTS */
  .dots {
    display: flex;
    gap: 8px;
    margin-top: 4px;
  }

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--brand, #FF6B00);
    animation: dotBounce 1.4s infinite;
  }

  .dot.d2 {
    animation-delay: .15s;
  }

  .dot.d3 {
    animation-delay: .30s;
  }

  @keyframes dotBounce {
    0% {
      transform: translateY(0);
      opacity: .7;
    }

    50% {
      transform: translateY(-9px);
      opacity: 1;
    }

    100% {
      transform: translateY(0);
      opacity: .7;
    }
  }

  /* DOODLE FLOATING ANIMATIONS */
  .float-a {
    animation: floatA 3.2s ease-in-out infinite;
  }

  @keyframes floatA {
    0% {
      transform: translateY(0);
    }

    50% {
      transform: translateY(-10px);
    }

    100% {
      transform: translateY(0);
    }
  }

  .float-b {
    animation: floatB 3.8s ease-in-out infinite;
  }

  @keyframes floatB {
    0% {
      transform: translateY(0);
    }

    50% {
      transform: translateY(12px);
    }

    100% {
      transform: translateY(0);
    }
  }

  .gear {
    animation: spinGear 6s linear infinite;
  }

  @keyframes spinGear {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .cursor {
    animation: cursorMove 2.4s ease-in-out infinite;
    transform-origin: center;
  }

  @keyframes cursorMove {
    0% {
      transform: translate(0, 0);
      opacity: .8;
    }

    50% {
      transform: translate(12px, -6px);
      opacity: 1;
    }

    100% {
      transform: translate(0, 0);
      opacity: .8;
    }
  }

  /* FADE SLIDE */
  .fade-slide-enter-active,
  .fade-slide-leave-active {
    transition: opacity .25s ease, transform .25s ease;
  }

  .fade-slide-enter-from,
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(12px);
  }

  .builder-screen {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* Header Bar */
  .builder-header {
    background: #fff;
    border: 1px solid var(--border);
    padding: 14px 18px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .header-actions {
    display: flex;
    gap: 10px;
  }

  /* Main Layout */
  .builder-body {
    display: flex;
    gap: 18px;
    height: 100vh;
  }

  /* Preview */
  .preview-area {
    flex: 1;
    display: flex;
    /* add */
    align-items: center;
    /* centers frame */
    justify-content: center;
    /* centers frame */
    height: 100%;
    /* add */
    position: relative;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: #fff;
    overflow: hidden;
  }

  .preview-frame {
    width: 100%;
    height: 100%;
    border: none;
  }

  /* Sidebar */
  .editor-sidebar {
    width: 310px;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--border);
    border-radius: 10px;
    background: #fff;
  }

  /* Tabs */
  .tab-row {
    display: flex;
    border-bottom: 1px solid var(--border);
  }

  .tab-item {
    flex: 1;
    text-align: center;
    padding: 10px;
    font-size: 13px;
    cursor: pointer;
    border-right: 1px solid var(--border);
  }

  .tab-item:last-child {
    border-right: none;
  }

  .tab-item.active {
    background: var(--primary);
    color: white;
  }

  /* Tab Content */
  .tab-content {
    padding: 14px;
    flex: 1;
    overflow-y: auto;
  }

  /* Section List */
  .section-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    /* 2 items per row */
    gap: 10px 12px;
  }

  .section-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 10px;
    cursor: grab;
    transition: .2s;
    width: 100%;
    font-size: .7rem;
    min-height: 3rem;
  }

  .section-item:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, .06);
  }

  .drag-handle {
    font-size: 17px;
    cursor: grab;
    color: var(--muted);
  }

  /* Drag states */
  .drag-ghost {
    opacity: 0.4;
    transform: scale(0.96);
  }

  .drag-chosen {
    background: #eef2ff;
    border-color: var(--primary);
  }

  /* Placeholder panel */
  .empty-panel {
    text-align: center;
    padding: 40px 10px;
    opacity: .7;
  }

  .tab-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }

  iframe {
    border: 0 !important;
  }

  .preview-live {
    overflow-y: auto;
    padding: 0;
    background: white;
    cursor: pointer;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    width: 100%;
    height: 100vh;
    display: block;
  }

  .preview-live * {
    transition: .15s ease;
  }

  /* Device Toggle Buttons */
  .device-toggle {
    display: flex;
    gap: 8px;
  }

  .device-toggle button {
    border: 1px solid var(--border);
    background: #fff;
    padding: 6px 9px;
    border-radius: 6px;
    cursor: pointer;
    transition: .25s ease;
    font-size: 15px;
  }

  .device-toggle button:hover {
    background: var(--primary-light);
    color: var(--primary);
  }

  .device-toggle button.active {
    background: var(--primary);
    border-color: var(--primary);
    color: #fff;
  }

  .active-section {
    border-color: #2563eb !important;
    background: #e8f0ff !important;
  }

  .section-item {
    transition: .2s;
  }

  /* Preview Device Responsive Modes */
  .preview-area.desktop .preview-live {
    width: 100%;
  }

  .preview-area.tablet .preview-live {
    width: 820px;
    margin: auto;
    border-radius: 12px;
    overflow: hidden;
  }

  .preview-area.mobile .preview-live {
    width: 420px;
    margin: auto;
    border-radius: 22px;
    overflow: hidden;
    border: 1px solid var(--border);
  }

  .builder-screen {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* ===== HEADER ===== */
  .builder-header {
    background: #fff;
    border: 1px solid var(--border);
    padding: 14px 22px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .title-area {
    display: flex;
    align-items: center;
    gap: 18px;
  }

  .title {
    font-size: 17px;
    font-weight: 600;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 6px;
  }

  /* ===== ACTION BUTTON ENHANCEMENT ===== */
  .btn-secondary.subtle:hover,
  .btn-primary.strong:hover {
    transform: translateY(-2px);
    transition: .2s;
  }

  /* ===== MAIN BODY LAYOUT ===== */
  .builder-body {
    display: flex;
    gap: 18px;
    height: 100vh;
  }

  /* ===== REUSABLE CARD STYLE ===== */
  .card {
    border: 1px solid var(--border);
    border-radius: 12px;
    background: #fff;
    overflow: hidden;
  }

  /* ===== PREVIEW PANEL ===== */
  .preview-area {
    flex: 1;
    position: relative;
  }

  .preview-live {
    width: 100%;
    height: 100%;
    border: none;
  }

  .preview-live.smooth {
    transition: .25s ease;
  }

  /* Loading preview */
  .preview-loader {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    opacity: .7;
  }

  /* ===== SIDEBAR ===== */
  .editor-sidebar {
    width: 310px;
    display: flex;
    flex-direction: column;
  }

  /* ===== TABS ===== */
  .tab-row {
    display: flex;
    border-bottom: 1px solid var(--border);
    background: #fafafa;
  }

  .tab-item {
    flex: 1;
    text-align: center;
    padding: 10px 4px;
    font-size: 12.8px;
    cursor: pointer;
    border-right: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: 2px;
    transition: .2s;
  }

  .tab-item:last-child {
    border-right: none;
  }

  .tab-item.active {
    background: #000;
    color: white;
    font-weight: 600;
    border-right: none;
  }

  /* ===== TAB CONTENT AREA ===== */
  .tab-content {
    padding: 14px;
    flex: 1;
    overflow-y: auto;
  }

  /* Section Items */
  .section-item {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 9px;
    padding: 10px 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    transition: .25s ease;
  }

  .section-item:hover {
    background: #fafafa;
  }

  .active-section {
    border-color: #000;
    background: #f1f1f1;
  }

  /* Tab buttons */
  .tab-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 14px;
  }

  /* Device toggle */
  .device-toggle button {
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 7px;
    padding: 6px 10px;
  }

  .device-toggle button.active {
    background: #000;
    color: #fff;
  }

  /* Device frame sizes */
  .preview-area.tablet .preview-live {
    width: 820px;
    margin: auto;
    border-radius: 12px;
  }

  .preview-area.mobile .preview-live {
    width: 430px;
    margin: auto;
    border-radius: 18px;
    border: 1px solid var(--border);
  }

  /* ---- SECTIONS UI UPGRADE ---- */
  .sections-ui-wrap {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .section-head h3 {
    font-size: 14px;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .section-head p {
    font-size: 11px;
    opacity: .7;
  }

  /* modern list look */
  .pro-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  /* card */
  .section-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 11px 14px;
    border-radius: 10px;
    background: #fff;
    border: 1px solid var(--border);
    transition: .22s ease;
    cursor: pointer;
  }

  .section-card:hover {
    border-color: var(--text);
    transform: translateY(-2px);
  }

  .section-selected {
    border-color: #000 !important;
    background: #fafafa;
    box-shadow: inset 0 0 0 1.4px #000;
  }

  /* left side info */
  .section-card .left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .section-card .drag-icon {
    font-size: 16px;
    color: var(--muted);
  }

  /* toggle button */
  .toggle-btn {
    height: 30px;
    width: 30px;
    border-radius: 8px;
    border: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    transition: .2s ease;
    background: #fff;
  }

  .toggle-btn.on:hover {
    background: #e8e8e8;
  }

  .toggle-btn.off {
    opacity: .45;
    filter: grayscale(1);
  }

  /* actions bar */
  .section-actions {
    display: flex;
    justify-content: space-between;
    margin-top: 4px;
  }

  .ghost {
    border-style: dashed !important;
    background: #fafafa !important;
  }

  .section-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;
    border: 1px solid var(--border);
    border-radius: 10px;
    padding: 10px 12px;
    cursor: pointer;
    transition: .25s ease;
  }

  .section-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 8px rgba(0, 0, 0, .07);
    border-color: var(--primary);
  }

  .section-item.active-section {
    border-color: var(--primary);
    background: rgba(37, 99, 235, 0.07);
  }

  /* Hidden section visual feedback */
  .hidden-section {
    opacity: 0.45;
    filter: grayscale(100%);
  }

  .left-col {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* Drag handle styling */
  .drag-handle {
    font-size: 20px;
    color: var(--muted);
    cursor: grab;
    display: flex;
  }

  /* Visibility toggle button */
  .toggle-visibility-btn {
    border: none;
    background: #f7f7f7;
    border-radius: 6px;
    padding: 6px 9px;
    cursor: pointer;
    font-size: .6rem;
    transition: .25s ease;
    font-size: 18px;
    display: flex;
    align-items: center;
  }

  .toggle-visibility-btn:hover {
    background: #e9e9e9;
    transform: scale(1.08);
  }

  .hidden-section .toggle-visibility-btn {
    background: #fff0f0;
  }

  /* =============================
   MOBILE RESPONSIVE PATCH
   ============================= */

  @media (max-width: 1024px) {
    .builder-body {
      flex-direction: column;
      height: auto;
    }

    .editor-sidebar {
      width: 100% !important;
      max-height: 380px;
      border-radius: 10px;
    }

    .preview-area {
      height: 70vh !important;
    }

    .preview-live {
      height: 100% !important;
    }
  }

  @media (max-width: 768px) {

    /* Header restructure */
    .builder-header {
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
    }

    .title-area {
      width: 100%;
      justify-content: space-between;
    }

    .header-actions {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    .device-toggle {
      margin-left: 0 !important;
    }

    /* Main body stack */
    .builder-body {
      flex-direction: column;
      gap: 14px;
      height: auto;
    }

    /* Preview box becomes full width */
    .preview-area {
      width: 100%;
      height: 65vh;
      border-radius: 12px;
    }

    /* Sidebar → full width */
    .editor-sidebar {
      width: 100% !important;
      min-height: 320px;
      max-height: 460px;
      overflow-y: auto;
    }

    /* Tabs adjust */
    .tab-item span {
      display: none;
      /* Only icons visible */
    }

    .tab-item i {
      font-size: 20px;
    }

    /* Section grid → 1 column */
    .section-list {
      grid-template-columns: 1fr !important;
    }
  }

  @media (max-width: 480px) {

    /* Reduce padding for tighter space */
    .builder-header {
      padding: 10px 14px;
    }

    .title {
      font-size: 15px;
    }

    .device-toggle button {
      padding: 4px 8px;
      font-size: 14px;
    }

    .header-actions button {
      font-size: 13px;
      padding: 6px 10px;
    }

    /* Preview becomes phone-like */
    .preview-area {
      height: 60vh;
    }

    /* Editor sidebar scroll fix */
    .editor-sidebar {
      max-height: 340px !important;
    }

    .tab-content {
      padding: 10px !important;
    }

    .section-item {
      font-size: 12px;
      padding: 8px;
    }

    .toggle-visibility-btn {
      font-size: 15px;
      padding: 5px 6px;
    }
  }

  /* COLLAPSIBLE SIDEBAR */
  .editor-sidebar {
    width: 310px;
    transition: all 0.28s ease-in-out;
    position: relative;
  }

  .editor-sidebar.collapsed {
    width: 0px !important;
    padding: 0 !important;
    opacity: 0;
    overflow: hidden;
  }

  /* FLOATING ROUND TOGGLE BUTTON */
  .sidebar-toggle-btn {
    position: absolute;
    bottom: 1rem;
    /* <-- bottom position */
    right: .5rem;
    transform: translateY(-50%);
    z-index: 9999;

    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: none;

    background: #111;
    color: #fff;
    font-size: 22px;

    display: flex;
    justify-content: center;
    align-items: center;

    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
  }

  .sidebar-toggle-btn:hover {
    background: var(--primary);
    transform: scale(1.08) translateY(-50%);
  }
</style>